import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  PlusIcon,
  EyeIcon,
  ChatAlt2Icon,
  PencilIcon,
  TrashIcon,
  FilterIcon,
  SearchIcon,
  CheckCircleIcon,
  ClockIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  LocationMarkerIcon,
  RefreshIcon,
  ExclamationCircleIcon
} from '@heroicons/react/outline';

const MyRFQs = () => {
  const { getUserRFQs, deleteRFQ } = useAuth();
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [rfqToDelete, setRfqToDelete] = useState(null);

  const loadRFQs = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getUserRFQs();
      if (result.success) {
        setRfqs(result.rfqs);
      }
    } catch (error) {
      console.error('Error loading RFQs:', error);
    }
    setLoading(false);
  }, [getUserRFQs]);

  // Handle delete button click
  const handleDeleteClick = (rfq) => {
    setRfqToDelete(rfq);
    setShowDeleteModal(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!rfqToDelete) return;

    setDeletingId(rfqToDelete.id);
    try {
      const result = await deleteRFQ(rfqToDelete.id);
      if (result.success) {
        // Remove the deleted RFQ from the local state
        setRfqs(prevRfqs => prevRfqs.filter(rfq => rfq.id !== rfqToDelete.id));
        setShowDeleteModal(false);
        setRfqToDelete(null);
      } else {
        alert(`Error deleting RFQ: ${result.error}`);
      }
    } catch (error) {
      console.error('Error deleting RFQ:', error);
      alert('Error deleting RFQ. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  // Handle delete cancellation
  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setRfqToDelete(null);
  };

  useEffect(() => {
    loadRFQs();
  }, [loadRFQs]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadRFQs();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [loadRFQs]);

  const filteredRFQs = rfqs.filter(rfq => {
    const rfqTitle = rfq.textContent || rfq.title || '';
    const rfqDescription = rfq.textContent || rfq.description || '';
    const rfqCategories = rfq.categories || [rfq.channel || 'General'];
    
    const matchesSearch = rfqTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         rfqDescription.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         rfqCategories.some(cat => cat.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || rfq.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="h-3 w-3 mr-1" />
            Active
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <ClockIcon className="h-3 w-3 mr-1" />
            Pending
          </span>
        );
      case 'closed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Closed
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-CC5500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                My RFQs
              </h1>
              <p className="text-gray-600">
                Manage your procurement requests
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={loadRFQs}
                disabled={loading}
                className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                <RefreshIcon className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            <Link
              to="/create-rfq"
              className="inline-flex items-center px-4 py-2 bg-CC5500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Create RFQ
            </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search RFQs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-CC5500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="md:w-48">
              <div className="relative">
                <FilterIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-CC5500 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* RFQs List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              {filteredRFQs.length} RFQ{filteredRFQs.length !== 1 ? 's' : ''} found
            </h2>
          </div>

          <div className="p-6">
            {filteredRFQs.length === 0 ? (
              <div className="text-center py-12">
                <div className="mx-auto h-16 w-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <SearchIcon className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {rfqs.length === 0 ? 'No RFQs yet' : 'No RFQs match your search'}
                </h3>
                <p className="text-gray-500 mb-6">
                  {rfqs.length === 0 
                    ? 'Create your first RFQ to get started'
                    : 'Try adjusting your search or filter criteria'
                  }
                </p>
                {rfqs.length === 0 && (
                  <Link
                    to="/create-rfq"
                    className="inline-flex items-center px-4 py-2 bg-CC5500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Create Your First RFQ
                  </Link>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {filteredRFQs.map((rfq) => (
                  <div key={rfq.id} className="border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {rfq.textContent || rfq.title || 'RFQ Title'}
                          </h3>
                          {getStatusBadge(rfq.status)}
                        </div>
                        
                        <p className="text-gray-600 mb-4">
                          {rfq.textContent || rfq.description || 'No description available'}
                        </p>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div className="flex items-center text-gray-500">
                            <CurrencyDollarIcon className="h-4 w-4 mr-2" />
                            <span>{rfq.budget ? `$${rfq.budget.toLocaleString()}` : 'Budget not specified'}</span>
                          </div>
                          <div className="flex items-center text-gray-500">
                            <CalendarIcon className="h-4 w-4 mr-2" />
                            <span>{new Date(rfq.timestamp).toLocaleDateString()}</span>
                          </div>
                          <div className="flex items-center text-gray-500">
                            <LocationMarkerIcon className="h-4 w-4 mr-2" />
                            <span>{rfq.location?.name || 'Not specified'}</span>
                          </div>
                          <div className="flex items-center text-gray-500">
                            <ChatAlt2Icon className="h-4 w-4 mr-2" />
                            <span>{rfq.replyCount || 0} responses</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 ml-4">
                        <button className="p-2 text-gray-400 hover:text-blue-600 transition-colors">
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        <button className="p-2 text-gray-400 hover:text-green-600 transition-colors">
                          <ChatAlt2Icon className="h-5 w-5" />
                        </button>
                        <button className="p-2 text-gray-400 hover:text-orange-600 transition-colors">
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button 
                          onClick={() => handleDeleteClick(rfq)}
                          disabled={deletingId === rfq.id}
                          className="p-2 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && rfqToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <ExclamationCircleIcon className="h-8 w-8 text-red-600" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">
                  Delete RFQ
                </h3>
                <p className="text-sm text-gray-500">
                  This action cannot be undone.
                </p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700">
                Are you sure you want to delete "<strong>{rfqToDelete.textContent || rfqToDelete.title || 'RFQ'}</strong>"?
              </p>
              <p className="text-sm text-gray-500 mt-2">
                This will permanently remove the RFQ and all associated responses.
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={handleDeleteCancel}
                disabled={deletingId === rfqToDelete.id}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deletingId === rfqToDelete.id}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center"
              >
                {deletingId === rfqToDelete.id ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Deleting...
                  </>
                ) : (
                  'Delete RFQ'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyRFQs; 